<template>
  <div class="flex flex-col h-full fadeIn">
    <DashboardHeader title="Applicant" link="/applicant" />

    <div class="flex-1 overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm">
      <!-- Main data to scroll -->
      <div class="h-full overflow-y-auto">
        <header
          class="sticky top-0 flex lg:flex-row flex-row-reverse justify-between items-center flex-wrap border-b bg-white z-10"
        >
          <!-- Tabs -->
          <nav class="flex gap-2 h-fit w-full lg:w-auto p-4" aria-label="Tabs">
            <button
              v-for="tab in applicantTabs"
              :key="tab.id"
              @click="setActiveTab(tab.id)"
              :class="[
                activeTab === tab.id
                  ? 'bg-cosmicPurple/20 text-cosmicPurple rounded-md'
                  : 'hover:bg-cosmicPurple/10 hover:text-cosmicPurple/90 text-gray-600 rounded-md',
                'whitespace-nowrap py-2 px-4 font-medium text-sm transition-all duration-200'
              ]"
            >
              {{ tab.name }}
            </button>
          </nav>
          <UserDetailsCard
            class="ml-auto"
            :user-details="applicantProfileStore.applicantProfile?.user_details"
          />
        </header>

        <!-- Program Status Tab Content -->
        <div v-if="(isGuide || isOwner) && activeTab === 'program-status'" class="p-4 px-6">
          <ApplicantData
            :applicant-profile="applicantProfileStore.applicantProfile"
            :user-details="applicantProfileStore.applicantProfile?.user_details"
          />
        </div>

        <!-- Applications Tab Content -->
        <div v-if="activeTab === 'applications'" >
          <JobApplicationsTable
            v-if="applicantProfileStore.applicantProfile?.user_details"
            :user-id="applicantProfileStore.applicantProfile.user_details.id.toString()"
            :applications="applicationsStore.userApplications"
            :current-page="applicationsStore.currentPage"
            :total-pages="totalPages"
            :is-loading="applicationsStore.isLoadingList"
            :show-pagination="true"
            :fetch-applications="changePage"
            @open-application-details="openApplicationDetails"
          />
        </div>

        <!-- Referral Package Tab Content -->
        <div v-if="(isGuide || isOwner) && activeTab === 'referral-package'" class="p-4 px-6">
          <ReferralPackageContent />
        </div>

        <!-- Resume Files Tab Content (guides or the applicant viewing their own profile) -->
        <div v-if="(isGuide || isOwner) && activeTab === 'resume'">
          <ResumeFilesContent
            :user-id="applicantProfileStore.applicantProfile?.user_details?.id"
            :is-owner="isOwner"
          />
        </div>
      </div>
    </div>

    <JobApplicationModal
      v-if="jobApplicationModal"
      v-model:is-open="jobApplicationModal"
      :application-id="applicationDetailsId"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import DashboardHeader from '@/components/common/DashboardHeader.vue'
import ReferralPackageContent from '@/components/referrals/ReferralPackageContent.vue'
import ResumeFilesContent from '@/components/applicants/ResumeFilesContent.vue'
import ApplicantData from '@/components/applicants/ApplicantData.vue'
import JobApplicationsTable from '@/components/common/jobApplication/JobApplicationsTable.vue'
import UserDetailsCard from '@/components/common/UserDetailsCard.vue'
import { useApplicantProfileStore } from '@/stores/applicantProfile'
import { useApplicationsStore } from '@/stores/applications'
import { useAuth } from '@/composables/useAuth'
import JobApplicationModal from '@/components/common/jobApplication/JobApplicationModal.vue'
import { useUserStore } from '@/stores/user'
import { useAuthStore } from '@/stores/auth'


const route = useRoute()
const router = useRouter()
const applicantProfileStore = useApplicantProfileStore()
const applicationsStore = useApplicationsStore()
const userStore = useUserStore()
const authStore = useAuthStore()

const { isGuide } = useAuth()
const isOwner = computed(() => {
  return applicantProfileStore.applicantProfile?.user_details.id === userStore.currentUser?.id
})

const activeTab = ref(route.query.tab as string || 'applications')

const jobApplicationModal = ref(false)
const applicationDetailsId = ref<string | null>(null)

const fellowMentorTabs = [{ id: 'applications', name: 'Applications' }]

const guideOrOwnerTabs = [
  { id: 'applications', name: 'Applications' },
  { id: 'program-status', name: 'Program Status' },
  { id: 'referral-package', name: 'Referral Package' },
  { id: 'resume', name: 'Resume' },
]

const applicantTabs = computed(() => {
  if (isGuide.value || isOwner.value) return guideOrOwnerTabs
  return fellowMentorTabs
})

const setActiveTab = (tabId: string) => {
  activeTab.value = tabId
  if (tabId === 'applications') {
    const queryWithoutTab = { ...route.query }
    delete queryWithoutTab.tab
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.replace({ query: queryWithoutTab })
  } else {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.replace({ query: { ...route.query, tab: tabId } })
  }
}

const openApplicationDetails = (applicationId: number) => {
  jobApplicationModal.value = true
  applicationDetailsId.value = applicationId.toString()
  
  // Update URL to include role_id if we have role information from the application
  const application = applicationsStore.userApplications.find(app => app.id === applicationId)
  if (application?.open_role_details?.id && route.params.role_id !== application.open_role_details.id.toString()) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.replace({
      name: 'Applicant Profile with Role',
      params: {
        id: route.params.id,
        role_id: application.open_role_details.id.toString()
      },
      query: route.query
    })
  }
}

const totalPages = computed(() =>
  Math.ceil(applicationsStore.userTotalCount / applicationsStore.pageSize)
)

const changePage = async (page: number) => {
  if (page < 1 || page > totalPages.value) return
  const userId = route.params.id as string

  applicationsStore.isLoadingList = true
  try {
    // Update store pagination state before fetching
    applicationsStore.currentPage = page
    
    await applicationsStore.fetchApplicationsByUserId(
      userId,
      page,
      applicationsStore.pageSize,
      applicationsStore.currentSort
    )
    
    // currentPage.value = page // Now using store's currentPage
  } finally {
    applicationsStore.isLoadingList = false
  }
}

const fetchApplicantProfile = async (userId: string) => {
  // Clear previous data immediately to prevent flash of old data
  applicationsStore.userApplications = []
  applicationsStore.isLoadingList = true

  try {
    await applicantProfileStore.fetchApplicantProfileById(userId)

    if (!applicationsStore.currentSort) {
      applicationsStore.currentSort = '-updated_at'
    }

    // Reset pagination state
    applicationsStore.currentPage = 1
    
    await applicationsStore.fetchApplicationsByUserId(
      userId,
      1,
      applicationsStore.pageSize,
      applicationsStore.currentSort
    )
    
    // currentPage.value = 1 // Now using store's currentPage
  } catch (error) {
    console.error('Error fetching applicant data:', error)
  } finally {
    applicationsStore.isLoadingList = false
  }
}

// Watch for route query changes to sync tab state (resume tab: guides or applicant owner only)
watch(
  () =>
    [
      route.query.tab,
      authStore.userRole,
      isOwner.value,
      applicantProfileStore.applicantProfile?.user_details?.id,
    ] as const,
  ([newTab, role]) => {
    const tab = (newTab as string) || 'applications'
    if (tab === 'resume') {
      if (!role) return
      const profileLoaded = !!applicantProfileStore.applicantProfile?.user_details?.id
      if (!profileLoaded) return
      const allowed = role === 'GUIDE' || isOwner.value
      if (!allowed) {
        activeTab.value = 'applications'
        const queryWithoutTab = { ...route.query }
        delete queryWithoutTab.tab
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        router.replace({ query: queryWithoutTab })
        return
      }
    }
    activeTab.value = tab
  }
)

// Watch for route parameter changes to handle navigation between applicants
watch(
  () => route.params.id,
  async (newId, oldId) => {
    if (newId && newId !== oldId) {
      await fetchApplicantProfile(newId as string)
    }
  },
  { immediate: true }
)

</script>
